import { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from '@/lib/auth-client';
import { CollaborativeCursor } from '@/components/collaborative-cursor';
import { AvatarStack } from '@/components/avatar';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RiPlayCircleLine, RiRecordCircleLine, RiAddLine, RiSubtractLine } from '@remixicon/react';
import { cn } from '@/lib/utils';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';

// Mock user database
const mockUserTemplates = [
  {
    id: 'mock-maxtro-001',
    name: 'Maxtro',
    email: 'maxtro@example.com',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Maxtro&backgroundColor=b6e3f4',
  },
  {
    id: 'mock-sarah-002',
    name: 'Sarah',
    email: 'sarah@example.com',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah&backgroundColor=ffd5dc',
  },
  {
    id: 'mock-alex-003',
    name: 'Alex',
    email: 'alex@example.com',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex&backgroundColor=c0aede',
  },
  {
    id: 'mock-jordan-004',
    name: 'Jordan',
    email: 'jordan@example.com',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jordan&backgroundColor=ffdfbf',
  },
  {
    id: 'mock-casey-005',
    name: 'Casey',
    email: 'casey@example.com',
    image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Casey&backgroundColor=d4f4dd',
  },
];

export function CollaborationDemoPage() {
  const { data: session } = useSession();
  const [activeMockUsers, setActiveMockUsers] = useState([mockUserTemplates[0]]); // Start with just Maxtro

  const canvasRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  // Use refs to avoid re-renders and maintain smooth cursor
  const userCursorRef = useRef<HTMLDivElement>(null);
  const mockCursorsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastMousePosRef = useRef({ x: 50, y: 50 });
  const mockPositionsRef = useRef<Record<string, { x: number; y: number }>>({
    [mockUserTemplates[0].id]: { x: 30, y: 30 },
  });

  // Direct DOM manipulation for zero-lag cursor tracking
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current || !userCursorRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Clamp values
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));

    lastMousePosRef.current = { x: clampedX, y: clampedY };

    // Direct DOM update - use left/top for position, not transform!
    userCursorRef.current.style.left = `${clampedX}%`;
    userCursorRef.current.style.top = `${clampedY}%`;
  }, []);

  // Add/remove mock users
  const addMockUser = useCallback(() => {
    const nextUser = mockUserTemplates.find(u => !activeMockUsers.some(au => au.id === u.id));
    if (nextUser) {
      setActiveMockUsers(prev => [...prev, nextUser]);
      mockPositionsRef.current[nextUser.id] = {
        x: 20 + Math.random() * 60,
        y: 20 + Math.random() * 60,
      };
    }
  }, [activeMockUsers]);

  const removeMockUser = useCallback(() => {
    if (activeMockUsers.length > 1) {
      const userToRemove = activeMockUsers[activeMockUsers.length - 1];
      setActiveMockUsers(prev => prev.slice(0, -1));
      delete mockPositionsRef.current[userToRemove.id];
    }
  }, [activeMockUsers]);

  // Animate all mock users' cursors with direct DOM manipulation - free movement
  useEffect(() => {
    let isAnimating = true;
    const userStates: Record<string, {
      currentX: number;
      currentY: number;
      targetX: number;
      targetY: number;
      speed: number;
    }> = {};

    // Initialize states for each active user with random targets
    activeMockUsers.forEach((user, index) => {
      const pos = mockPositionsRef.current[user.id] || { x: 30, y: 30 };
      userStates[user.id] = {
        currentX: pos.x,
        currentY: pos.y,
        targetX: 20 + Math.random() * 60,
        targetY: 20 + Math.random() * 60,
        speed: 0.015 + Math.random() * 0.015, // Random speed for each user
      };
    });

    const animate = () => {
      if (!isAnimating) return;

      activeMockUsers.forEach((user) => {
        const state = userStates[user.id];
        const cursorElement = mockCursorsRef.current.get(user.id);

        if (!state || !cursorElement) return;

        // Move towards target
        const dx = state.targetX - state.currentX;
        const dy = state.targetY - state.currentY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // If close to target, pick a new random target
        if (distance < 2) {
          state.targetX = 10 + Math.random() * 80;
          state.targetY = 10 + Math.random() * 80;
        }

        // Smooth interpolation towards target
        state.currentX += dx * state.speed;
        state.currentY += dy * state.speed;

        // Clamp values
        state.currentX = Math.max(5, Math.min(95, state.currentX));
        state.currentY = Math.max(5, Math.min(95, state.currentY));

        // Direct DOM update - use left/top for position!
        cursorElement.style.left = `${state.currentX}%`;
        cursorElement.style.top = `${state.currentY}%`;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      isAnimating = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [activeMockUsers]);

  // Get user info with fallback
  const currentUser = session?.user
    ? {
        id: session.user.id,
        name: session.user.name || 'You',
        email: session.user.email,
        image: session.user.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${session.user.email}`,
      }
    : {
        id: 'demo-user',
        name: 'Demo User',
        email: 'demo@example.com',
        image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=DemoUser',
      };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="flex flex-col overflow-hidden">
        {/* Header */}
        <header
          className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur-xl px-4 z-10"
          style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
        >
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4 mr-2" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Collaboration Demo</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Badge variant="outline" className="gap-2 ml-4">
            <RiRecordCircleLine className="h-3.5 w-3.5 text-red-500 animate-pulse" />
            Demo Mode
          </Badge>

          <div className="flex items-center gap-4 ml-auto">
            {/* User Controls */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={removeMockUser}
                disabled={activeMockUsers.length <= 1}
                className="h-8 w-8 p-0"
              >
                <RiSubtractLine className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={addMockUser}
                disabled={activeMockUsers.length >= mockUserTemplates.length}
                className="h-8 w-8 p-0"
              >
                <RiAddLine className="h-4 w-4" />
              </Button>
            </div>

            {/* Active Users */}
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  {activeMockUsers.length + 1} Active
                </span>
                <AvatarStack animate size={32}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar className="cursor-pointer transition-transform hover:scale-110 hover:-translate-y-1">
                        <AvatarImage src={currentUser.image} />
                        <AvatarFallback>{currentUser.name.slice(0, 2)}</AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="font-medium">
                      {currentUser.name} (You)
                    </TooltipContent>
                  </Tooltip>
                  {activeMockUsers.map(user => (
                    <Tooltip key={user.id}>
                      <TooltipTrigger asChild>
                        <Avatar className="cursor-pointer transition-transform hover:scale-110 hover:-translate-y-1">
                          <AvatarImage src={user.image} />
                          <AvatarFallback>{user.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="font-medium">
                        {user.name}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </AvatarStack>
              </div>
            </TooltipProvider>

            <ModeToggle />
          </div>
        </header>

      {/* Collaboration Canvas */}
      <div
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        className={cn(
          'flex-1 relative overflow-hidden',
          // Hide default cursor
          'cursor-none',
          // Simple background - better performance
          'bg-muted/30'
        )}
        style={{
          // GPU-accelerated background pattern
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--muted)) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      >
        {/* Simplified overlay - static, won't repaint */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--primary) / 0.03) 0%, transparent 50%, hsl(var(--secondary) / 0.03) 100%)',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
          }}
        />

        {/* Info Card */}
        <div
          className="absolute top-8 left-8 max-w-sm"
          style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
        >
          <div className="bg-card/98 border-2 border-border/50 rounded-xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <RiPlayCircleLine className="h-4 w-4 text-primary" />
              </div>
              <h2 className="font-semibold">Real-Time Collaboration</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              See cursors and activity from your team in real-time
            </p>
            <div className="text-xs text-muted-foreground">
              Use <kbd className="px-1.5 py-0.5 rounded bg-muted border">+</kbd> and{' '}
              <kbd className="px-1.5 py-0.5 rounded bg-muted border">-</kbd> to add or remove users
            </div>
          </div>
        </div>

        {/* User's Cursor - using ref for zero-lag */}
        <div
          ref={userCursorRef}
          className="absolute pointer-events-none z-50"
          style={{ left: '50%', top: '50%' }}
        >
          <CollaborativeCursor
            userId={currentUser.id}
            userName={currentUser.name}
            userAvatar={currentUser.image}
            position={{ x: 0, y: 0 }} // Position handled by parent div
            color="text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/50"
          />
        </div>

        {/* Mock Users' Cursors - using refs for zero-lag */}
        {activeMockUsers.map((user, index) => {
          const initialPos = mockPositionsRef.current[user.id] || { x: 30, y: 30 };
          return (
            <div
              key={user.id}
              ref={(el) => {
                if (el) {
                  mockCursorsRef.current.set(user.id, el);
                } else {
                  mockCursorsRef.current.delete(user.id);
                }
              }}
              className="absolute pointer-events-none z-50"
              style={{ left: `${initialPos.x}%`, top: `${initialPos.y}%` }}
            >
              <CollaborativeCursor
                userId={user.id}
                userName={user.name}
                userAvatar={user.image}
                position={{ x: 0, y: 0 }} // Position handled by parent div
              />
            </div>
          );
        })}
      </div>

        {/* Footer with instructions */}
        <footer
          className="px-6 py-3 border-t bg-card/95"
          style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
        >
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <span>💡 Move your mouse to interact</span>
          </div>
        </footer>
      </SidebarInset>
    </SidebarProvider>
  );
}
